import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="p-5 min-h-full bg-[#F7FAFC]">
      <div class="max-w-6xl mx-auto">
        <div class="flex items-center justify-between gap-4 mb-5 shrink-0">
          <div>
            <h1 class="text-xl font-semibold text-slate-900 tracking-tight">Company Settings</h1>
            <p class="text-slate-500 text-xs">Manage business identity, PDF branding, and document defaults.</p>
          </div>
          <button [disabled]="saving()" (click)="saveSettings()" class="h-10 px-4 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
            @if (saving()) { <i class="ti ti-loader animate-spin text-base"></i> Saving... }
            @else { <i class="ti ti-device-floppy text-base"></i> Save Settings }
          </button>
        </div>

        <div class="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5">
          <main class="space-y-5">
            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
              <div class="px-5 py-4 border-b border-slate-100">
                <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Business Details</h2>
                <p class="text-xs text-slate-500 mt-1">These details appear across customer-facing PDFs and emails.</p>
              </div>
              <div class="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <label class="block">
                  <span class="field-label">Legal Company Name</span>
                  <input type="text" [(ngModel)]="settings().company_name" class="field-input">
                </label>
                <label class="block">
                  <span class="field-label">Trading Email</span>
                  <input type="email" [(ngModel)]="settings().company_email" class="field-input">
                </label>
                <label class="block">
                  <span class="field-label">Contact Number</span>
                  <input type="text" [(ngModel)]="settings().company_phone" class="field-input">
                </label>
                <label class="block md:col-span-2">
                  <span class="field-label">Physical Address</span>
                  <input type="text" [(ngModel)]="settings().company_address" class="field-input">
                </label>
              </div>
            </section>

            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
              <div class="px-5 py-4 border-b border-slate-100">
                <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Notification Center</h2>
                <p class="text-xs text-slate-500 mt-1">Control which operational tasks create app alerts or send emails.</p>
              </div>
              <div class="p-5 space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label class="toggle-row">
                    <input type="checkbox" [(ngModel)]="settings().email_notifications_enabled">
                    <span>
                      <strong>Email notifications</strong>
                      <small>Allows customer, supplier, staff, OTP, assignment, and document emails.</small>
                    </span>
                  </label>
                  <label class="toggle-row">
                    <input type="checkbox" [(ngModel)]="settings().in_app_notifications_enabled">
                    <span>
                      <strong>In-app notification inbox</strong>
                      <small>Allows persistent admin/mobile bell notifications for action-required items.</small>
                    </span>
                  </label>
                </div>

                <div class="rounded-lg border border-slate-200 overflow-hidden">
                  <div class="grid grid-cols-[minmax(0,1fr)_72px_72px] bg-slate-50 border-b border-slate-200">
                    <div class="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Task</div>
                    <div class="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">App</div>
                    <div class="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Email</div>
                  </div>
                  @for (item of notificationControls; track item.key) {
                    <div class="grid grid-cols-[minmax(0,1fr)_72px_72px] border-b border-slate-100 last:border-b-0 items-center">
                      <div class="px-3 py-3 min-w-0">
                        <div class="flex items-center gap-2 mb-1">
                          <span class="w-7 h-7 rounded-md flex items-center justify-center" [ngClass]="item.iconClass">
                            <i class="ti text-[14px]" [class]="'ti-' + item.icon"></i>
                          </span>
                          <div class="min-w-0">
                            <p class="text-[12px] font-bold text-slate-900 leading-tight">{{ item.label }}</p>
                            <p class="text-[10px] text-slate-500 leading-snug">{{ item.description }}</p>
                          </div>
                        </div>
                      </div>
                      <label class="switch-cell" [class.opacity-40]="!settings().in_app_notifications_enabled">
                        <input type="checkbox" [ngModel]="settings()[item.appKey]" (ngModelChange)="setNotificationToggle(item.appKey, $event)" [disabled]="!settings().in_app_notifications_enabled">
                        <span></span>
                      </label>
                      <label class="switch-cell" [class.opacity-40]="!settings().email_notifications_enabled || !item.hasEmail">
                        <input type="checkbox" [ngModel]="settings()[item.emailKey]" (ngModelChange)="setNotificationToggle(item.emailKey, $event)" [disabled]="!settings().email_notifications_enabled || !item.hasEmail">
                        <span></span>
                      </label>
                    </div>
                  }
                </div>

                <div class="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3">
                  <p class="text-xs font-bold text-sky-900">Review requests are sent manually</p>
                  <p class="text-[11px] text-sky-700 mt-1">After field work finishes, the assigned plumber chooses Send Review Request or Remind Me Later. HQ can also send it from the job.</p>
                </div>
                <label class="block">
                  <span class="field-label">Global outgoing CC email</span>
                  <input type="email" [(ngModel)]="settings().outgoing_cc_email" placeholder="accounts@example.com" class="field-input">
                  <span class="block mt-2 text-[10px] font-semibold text-slate-400">Leave blank to disable global CC. The address is skipped when it is already the main recipient.</span>
                </label>
              </div>
            </section>

            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
              <div class="px-5 py-4 border-b border-slate-100">
                <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Google Maps</h2>
                <p class="text-xs text-slate-500 mt-1">Used for address search, dispatch map, and plumber/fleet location visibility.</p>
              </div>
              <div class="p-5 space-y-4">
                <label class="block">
                  <span class="field-label">Browser API Key</span>
                  <input type="text" [(ngModel)]="settings().google_maps_browser_api_key" placeholder="Google Maps JavaScript API key" class="field-input">
                  <span class="block mt-2 text-[10px] font-semibold text-slate-400">Enable Maps JavaScript API and Places API for this key. Restrict it to the admin/mobile domains.</span>
                </label>
                <label class="block">
                  <span class="field-label">Google Business review link</span>
                  <input type="url" [(ngModel)]="settings().google_business_review_url" placeholder="https://g.page/r/.../review" class="field-input">
                  <span class="block mt-2 text-[10px] font-semibold text-slate-400">Paste the “Ask for reviews” link from the NZ Plumbing Google Business Profile. Feedback redirects and the plumber QR code use this same link.</span>
                </label>
              </div>
            </section>

            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
              <div class="px-5 py-4 border-b border-slate-100">
                <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">PDF Defaults</h2>
                <p class="text-xs text-slate-500 mt-1">Shared defaults for quotes, declarations, and generated documents.</p>
              </div>
              <div class="p-5 space-y-4">
                <label class="block">
                  <span class="field-label">Standard Payment Terms</span>
                  <textarea [(ngModel)]="settings().pdf_terms" rows="5" class="field-input h-auto py-2 resize-none leading-relaxed"></textarea>
                </label>
              </div>
            </section>
          </main>

          <aside class="space-y-5">
            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
              <div class="px-5 py-4 border-b border-slate-100">
                <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Brand Logo</h2>
                <p class="text-xs text-slate-500 mt-1">Used on quotation and declaration PDFs.</p>
              </div>
              <div class="p-5">
                <button type="button" (click)="logoInput.click()" class="w-full h-40 rounded-xl border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center p-4 cursor-pointer hover:border-[#2B9AD2] hover:bg-white transition-colors group overflow-hidden relative">
                  @if (settings().company_logo_url) {
                    <img [src]="settings().company_logo_url" alt="Logo Preview" class="max-h-full max-w-full object-contain">
                    <span class="absolute bottom-3 rounded-md bg-slate-900/75 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white opacity-0 group-hover:opacity-100 transition-opacity">Change Logo</span>
                  } @else {
                    <span class="text-slate-400 text-sm font-semibold flex flex-col items-center">
                      <i class="ti ti-upload text-2xl mb-1 group-hover:scale-110 transition-transform"></i>
                      Upload Logo
                    </span>
                  }
                </button>
                <input #logoInput type="file" (change)="onLogoSelected($event)" accept="image/*" class="hidden">
                <p class="text-[10px] text-slate-400 mt-2 font-semibold">Transparent PNG is preferred. Maximum 1MB.</p>
              </div>
            </section>

            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-5">
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Document Footer</p>
              <p class="text-sm font-semibold text-slate-900 mt-2">2026-27 licence footer remains included in quotes and declarations.</p>
            </section>
          </aside>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .field-label {
      display: block;
      margin-bottom: .375rem;
      font-size: .75rem;
      font-weight: 700;
      color: #475569;
    }
    .field-input {
      width: 100%;
      height: 2.5rem;
      border-radius: .5rem;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      padding: 0 .75rem;
      font-size: .875rem;
      color: #1e293b;
      outline: none;
    }
    .field-input:focus {
      border-color: #2B9AD2;
      background: #fff;
    }
    .toggle-row {
      display: flex;
      gap: .75rem;
      align-items: flex-start;
      border: 1px solid #e2e8f0;
      border-radius: .75rem;
      background: #f8fafc;
      padding: .875rem;
      cursor: pointer;
    }
    .toggle-row input {
      margin-top: .15rem;
      width: 1rem;
      height: 1rem;
      accent-color: #1A6FA6;
    }
    .toggle-row strong {
      display: block;
      font-size: .8125rem;
      color: #0f172a;
    }
    .toggle-row small {
      display: block;
      margin-top: .2rem;
      font-size: .6875rem;
      line-height: 1.35;
      color: #64748b;
    }
    .switch-cell {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 56px;
      cursor: pointer;
    }
    .switch-cell input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }
    .switch-cell span {
      width: 36px;
      height: 20px;
      border-radius: 999px;
      background: #cbd5e1;
      position: relative;
      transition: .18s ease;
    }
    .switch-cell span::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      top: 2px;
      left: 2px;
      border-radius: 999px;
      background: white;
      box-shadow: 0 1px 2px rgba(15,23,42,.18);
      transition: .18s ease;
    }
    .switch-cell input:checked + span {
      background: #1A6FA6;
    }
    .switch-cell input:checked + span::after {
      transform: translateX(16px);
    }
    .switch-cell input:disabled + span {
      background: #e2e8f0;
    }
  `]
})
export class AdminSettingsComponent implements OnInit {
  notificationControls = [
    { key: 'job_assignment', label: 'Job assignments', description: 'Worker alerts when a job is allocated or changed.', icon: 'briefcase', iconClass: 'bg-sky-50 text-[#1A6FA6]', hasEmail: true, appKey: 'notify_job_assignment_app', emailKey: 'notify_job_assignment_email', appDefault: true, emailDefault: true },
    { key: 'project_assignment', label: 'Project work assignments', description: 'Worker alerts for project allocations and scheduled project work.', icon: 'building-skyscraper', iconClass: 'bg-violet-50 text-violet-600', hasEmail: true, appKey: 'notify_project_assignment_app', emailKey: 'notify_project_assignment_email', appDefault: true, emailDefault: true },
    { key: 'leave_requests', label: 'Leave requests', description: 'Admin review alerts and employee decision updates.', icon: 'calendar-time', iconClass: 'bg-amber-50 text-amber-600', hasEmail: true, appKey: 'notify_leave_requests_app', emailKey: 'notify_leave_requests_email', appDefault: true, emailDefault: true },
    { key: 'fleet_updates', label: 'Fleet updates', description: 'Vehicle assignment and fleet responsibility alerts.', icon: 'truck', iconClass: 'bg-teal-50 text-teal-600', hasEmail: false, appKey: 'notify_fleet_updates_app', emailKey: 'notify_fleet_updates_email', appDefault: true, emailDefault: false },
    { key: 'project_phase_reviews', label: 'Project phase reviews', description: 'Needs-changes and office review alerts for project forms.', icon: 'clipboard-check', iconClass: 'bg-indigo-50 text-indigo-600', hasEmail: true, appKey: 'notify_project_phase_reviews_app', emailKey: 'notify_project_phase_reviews_email', appDefault: true, emailDefault: true },
    { key: 'quote_responses', label: 'Quote responses', description: 'Admin alerts when customers accept or decline quotes.', icon: 'file-dollar', iconClass: 'bg-emerald-50 text-emerald-600', hasEmail: false, appKey: 'notify_quote_responses_app', emailKey: 'notify_quote_responses_email', appDefault: true, emailDefault: false },
    { key: 'customer_feedback', label: 'Customer feedback', description: 'Feedback request emails and submitted feedback alerts.', icon: 'message-star', iconClass: 'bg-rose-50 text-rose-600', hasEmail: true, appKey: 'notify_customer_feedback_app', emailKey: 'notify_customer_feedback_email', appDefault: true, emailDefault: true },
    { key: 'document_updates', label: 'Documents and AI', description: 'Document, declaration, insurance and as-built workflow alerts.', icon: 'folders', iconClass: 'bg-slate-100 text-slate-600', hasEmail: false, appKey: 'notify_document_updates_app', emailKey: 'notify_document_updates_email', appDefault: true, emailDefault: false },
    { key: 'safety_compliance', label: 'Safety and compliance', description: 'Audit, visa and safety action alerts.', icon: 'shield-check', iconClass: 'bg-orange-50 text-orange-600', hasEmail: false, appKey: 'notify_safety_compliance_app', emailKey: 'notify_safety_compliance_email', appDefault: true, emailDefault: false },
    { key: 'operations_alerts', label: 'Operations alerts', description: 'Long-waiting unassigned jobs and idle active-shift alerts.', icon: 'alarm', iconClass: 'bg-rose-50 text-rose-600', hasEmail: false, appKey: 'notify_operations_alerts_app', emailKey: 'notify_operations_alerts_email', appDefault: true, emailDefault: false },
  ];

  settings = signal<any>({
    company_name: '',
    company_email: '',
    outgoing_cc_email: '',
    company_phone: '',
    company_address: '',
    google_maps_browser_api_key: '',
    google_business_review_url: '',
    email_notifications_enabled: true,
    in_app_notifications_enabled: true,
    feedback_request_on_ready_for_office: true,
    feedback_request_on_completed: false,
    notify_job_assignment_app: true,
    notify_job_assignment_email: true,
    notify_project_assignment_app: true,
    notify_project_assignment_email: true,
    notify_leave_requests_app: true,
    notify_leave_requests_email: true,
    notify_fleet_updates_app: true,
    notify_fleet_updates_email: false,
    notify_project_phase_reviews_app: true,
    notify_project_phase_reviews_email: true,
    notify_quote_responses_app: true,
    notify_quote_responses_email: false,
    notify_customer_feedback_app: true,
    notify_customer_feedback_email: true,
    notify_document_updates_app: true,
    notify_document_updates_email: false,
    notify_safety_compliance_app: true,
    notify_safety_compliance_email: false,
    notify_operations_alerts_app: true,
    notify_operations_alerts_email: false,
    company_logo_url: '',
    pdf_terms: '',
    invoice_prefix: 'INV-',
    invoice_next_number: '2001'
  });

  saving = signal(false);
  private api = inject(ApiService);

  ngOnInit() {
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          const s = res.data;
          const notificationSettings = this.notificationControls.reduce((acc: any, item: any) => {
            acc[item.appKey] = this.toBool(s[item.appKey], item.appDefault);
            acc[item.emailKey] = this.toBool(s[item.emailKey], item.emailDefault);
            return acc;
          }, {});
          this.settings.set({
            company_name: s.company_name || '',
            company_email: s.company_email || '',
            outgoing_cc_email: s.outgoing_cc_email || '',
            company_phone: s.company_phone || '',
            company_address: s.company_address || '',
            google_maps_browser_api_key: s.google_maps_browser_api_key || '',
            google_business_review_url: s.google_business_review_url || '',
            email_notifications_enabled: this.toBool(s.email_notifications_enabled, true),
            in_app_notifications_enabled: this.toBool(s.in_app_notifications_enabled, true),
            feedback_request_on_ready_for_office: this.toBool(s.feedback_request_on_ready_for_office, true),
            feedback_request_on_completed: this.toBool(s.feedback_request_on_completed, false),
            ...notificationSettings,
            company_logo_url: s.company_logo_url || '',
            pdf_terms: s.pdf_terms || '',
            invoice_prefix: s.invoice_prefix || 'INV-',
            invoice_next_number: s.invoice_next_number || '2001'
          });
        }
      }
    });
  }

  onLogoSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      showToast('error', 'File is too large. Max 1MB allowed.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.settings.update(s => ({ ...s, company_logo_url: reader.result as string }));
    };
    reader.readAsDataURL(file);
  }

  setNotificationToggle(key: string, value: boolean) {
    this.settings.update(s => ({ ...s, [key]: !!value }));
  }

  saveSettings() {
    const ccEmail = String(this.settings().outgoing_cc_email || '').trim();
    if (ccEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ccEmail)) {
      showToast('error', 'Enter a valid global CC email address.');
      return;
    }
    const googleReviewUrl = String(this.settings().google_business_review_url || '').trim();
    if (googleReviewUrl) {
      try {
        const parsed = new URL(googleReviewUrl);
        if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('Invalid protocol');
      } catch {
        showToast('error', 'Enter a valid Google Business review link.');
        return;
      }
    }
    this.settings.update(s => ({ ...s, outgoing_cc_email: ccEmail, google_business_review_url: googleReviewUrl }));
    this.saving.set(true);
    this.api.updateCompanySettings(this.normalizedSettings()).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res.success) {
          showToast('success', 'Company settings saved.');
        } else {
          showToast('error', res.message || 'Failed to save settings.');
        }
      },
      error: () => {
        this.saving.set(false);
        showToast('error', 'Network error while saving settings.');
      }
    });
  }

  private normalizedSettings() {
    const s = this.settings();
    const payload: any = {
      ...s,
      email_notifications_enabled: s.email_notifications_enabled ? '1' : '0',
      in_app_notifications_enabled: s.in_app_notifications_enabled ? '1' : '0',
      feedback_request_on_ready_for_office: s.feedback_request_on_ready_for_office ? '1' : '0',
      feedback_request_on_completed: s.feedback_request_on_completed ? '1' : '0'
    };
    this.notificationControls.forEach(item => {
      payload[item.appKey] = s[item.appKey] ? '1' : '0';
      payload[item.emailKey] = s[item.emailKey] ? '1' : '0';
    });
    return payload;
  }

  private toBool(value: any, fallback: boolean): boolean {
    if (value === undefined || value === null || value === '') return fallback;
    const normalized = String(value).trim().toLowerCase();
    if (['1', 'true', 'yes', 'on', 'enabled'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off', 'disabled'].includes(normalized)) return false;
    return fallback;
  }
}
